// =============================================================================
// VandieZ shipper-web -- Bank accounts (/bank-accounts)
// =============================================================================
// CRUD over /users/me/bank-accounts. Shippers use bank accounts for
// refunds + post-launch settlement. Only the account holder name +
// IFSC + last-4 surface client-side; the full account number is
// Fernet-encrypted at rest on the backend.
// =============================================================================

import {
  AlertCircle,
  CheckCircle2,
  Landmark,
  Loader2,
  Plus,
  Star,
  Trash2,
} from "lucide-react";
import type React from "react";
import { useEffect, useState } from "react";

import { bankAccountsApi } from "@/api/client";
import { PageHeader } from "@/components/AppShell";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/lib/auth";
import { useToast } from "@/lib/toast";
import { cn } from "@/lib/utils";
import { ApiError } from "@/types/auth";
import type {
  BankAccountCreatePayload,
  BankAccountRead,
} from "@/types/user";

interface FormState {
  account_holder_name: string;
  account_number: string;
  ifsc_code: string;
  bank_name: string;
  branch_name: string;
  upi_handle: string;
  is_primary: boolean;
}

function emptyForm(): FormState {
  return {
    account_holder_name: "",
    account_number: "",
    ifsc_code: "",
    bank_name: "",
    branch_name: "",
    upi_handle: "",
    is_primary: false,
  };
}

export function BankAccountsPage(): React.JSX.Element {
  const { accessToken } = useAuth();
  const toast = useToast();
  const [items, setItems] = useState<BankAccountRead[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const [showForm, setShowForm] = useState<boolean>(false);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  async function refresh(): Promise<void> {
    if (accessToken === null) {return;}
    try {
      setIsLoading(true);
      const res = await bankAccountsApi.list(accessToken);
      setItems(res.items);
      setError(null);
    } catch (err: unknown) {
      setError(
        err instanceof ApiError
          ? err.problem.detail
          : "Couldn't load bank accounts.",
      );
    } finally {
      setIsLoading(false);
    }
  }

  function update<K extends keyof FormState>(field: K, value: FormState[K]): void {
    setForm((f) => ({ ...f, [field]: value }));
  }

  async function onCreate(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (accessToken === null) {return;}

    const payload: BankAccountCreatePayload = {
      account_holder_name: form.account_holder_name.trim(),
      account_number: form.account_number.trim(),
      ifsc_code: form.ifsc_code.trim().toUpperCase(),
      bank_name: form.bank_name.trim(),
      branch_name:
        form.branch_name.trim().length === 0 ? null : form.branch_name.trim(),
      upi_handle:
        form.upi_handle.trim().length === 0 ? null : form.upi_handle.trim(),
      is_primary: form.is_primary,
    };

    setIsSaving(true);
    try {
      await bankAccountsApi.create(accessToken, payload);
      toast.success("Bank account added.");
      setForm(emptyForm);
      setShowForm(false);
      await refresh();
    } catch (err: unknown) {
      const detail =
        err instanceof ApiError ? err.problem.detail : "Couldn't add account.";
      toast.error(detail);
    } finally {
      setIsSaving(false);
    }
  }

  async function onSetPrimary(id: string): Promise<void> {
    if (accessToken === null) {return;}
    setBusyId(id);
    try {
      await bankAccountsApi.setPrimary(accessToken, id);
      toast.success("Primary account updated.");
      await refresh();
    } catch (err: unknown) {
      toast.error(
        err instanceof ApiError
          ? err.problem.detail
          : "Couldn't set as primary.",
      );
    } finally {
      setBusyId(null);
    }
  }

  async function onDelete(id: string): Promise<void> {
    if (accessToken === null) {return;}
    // eslint-disable-next-line no-alert
    if (!window.confirm("Remove this bank account?")) {return;}
    setBusyId(id);
    try {
      await bankAccountsApi.remove(accessToken, id);
      toast.success("Bank account removed.");
      await refresh();
    } catch (err: unknown) {
      toast.error(
        err instanceof ApiError ? err.problem.detail : "Couldn't remove.",
      );
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Bank accounts"
        description="Used for refunds and post-launch settlement. Full account numbers are encrypted at rest."
        actions={
          <Button
            onClick={() => { setShowForm((v) => !v); }}
            variant={showForm ? "outline" : "default"}
          >
            <Plus className="h-4 w-4" />
            {showForm ? "Cancel" : "Add account"}
          </Button>
        }
      />

      {error !== null ? (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}

      {showForm ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">New bank account</CardTitle>
            <CardDescription>
              We encrypt the full account number; only the last 4 digits
              are kept in the clear.
            </CardDescription>
          </CardHeader>
          <form onSubmit={(e) => void onCreate(e)}>
            <CardContent className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="account_holder_name">Account holder name</Label>
                <Input
                  id="account_holder_name"
                  value={form.account_holder_name}
                  onChange={(e) => { update("account_holder_name", e.target.value); }}
                  required
                  maxLength={200}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="account_number">Account number</Label>
                <Input
                  id="account_number"
                  inputMode="numeric"
                  pattern="[0-9]{4,20}"
                  value={form.account_number}
                  onChange={(e) => { update("account_number", e.target.value); }}
                  required
                  maxLength={20}
                  className="font-mono"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ifsc_code">IFSC</Label>
                <Input
                  id="ifsc_code"
                  value={form.ifsc_code}
                  onChange={(e) => { update("ifsc_code", e.target.value.toUpperCase()); }}
                  required
                  maxLength={11}
                  className="font-mono"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="bank_name">Bank name</Label>
                <Input
                  id="bank_name"
                  value={form.bank_name}
                  onChange={(e) => { update("bank_name", e.target.value); }}
                  required
                  maxLength={100}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="branch_name">Branch name (optional)</Label>
                <Input
                  id="branch_name"
                  value={form.branch_name}
                  onChange={(e) => { update("branch_name", e.target.value); }}
                  maxLength={100}
                />
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="upi_handle">UPI handle (optional)</Label>
                <Input
                  id="upi_handle"
                  value={form.upi_handle}
                  onChange={(e) => { update("upi_handle", e.target.value); }}
                  placeholder="name@bank"
                />
              </div>
              <div className="flex items-center gap-2 sm:col-span-2">
                <input
                  id="is_primary"
                  type="checkbox"
                  checked={form.is_primary}
                  onChange={(e) => { update("is_primary", e.target.checked); }}
                  className="h-4 w-4"
                />
                <Label htmlFor="is_primary" className="m-0 cursor-pointer">
                  Make this my primary account
                </Label>
              </div>
            </CardContent>
            <div className="flex justify-end gap-2 p-6 pt-0">
              <Button
                type="button"
                variant="outline"
                onClick={() => { setShowForm(false); }}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSaving}>
                {isSaving ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Adding…
                  </>
                ) : (
                  "Add account"
                )}
              </Button>
            </div>
          </form>
        </Card>
      ) : null}

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : items === null || items.length === 0 ? (
        <Card>
          <CardHeader className="items-center text-center">
            <Landmark className="h-10 w-10 text-muted-foreground" />
            <CardTitle>No bank accounts yet</CardTitle>
            <CardDescription>
              Add a bank account to receive refunds and post-launch
              settlement payouts.
            </CardDescription>
          </CardHeader>
        </Card>
      ) : (
        <div className="space-y-3">
          {items.map((a) => (
            <Card key={a.id}>
              <CardContent className="flex flex-col gap-3 py-4 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
                <div className="flex items-start gap-3 min-w-0">
                  <Landmark className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium">
                      {a.bank_name}
                      {a.branch_name !== null && a.branch_name.length > 0
                        ? ` · ${a.branch_name}`
                        : ""}
                    </p>
                    <p className="font-mono text-xs text-muted-foreground">
                      ****{a.account_number_last_4} · {a.ifsc_code}
                      {a.upi_handle !== null && a.upi_handle.length > 0
                        ? ` · ${a.upi_handle}`
                        : ""}
                    </p>
                    <div className="mt-1 flex items-center gap-2">
                      {a.is_primary ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-success-subtle px-2 py-0.5 text-xs font-medium text-success-subtle-foreground">
                          <Star className="h-3 w-3" />
                          Primary
                        </span>
                      ) : null}
                      {a.verified ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-primary-subtle px-2 py-0.5 text-xs font-medium text-primary-subtle-foreground">
                          <CheckCircle2 className="h-3 w-3" />
                          Verified
                        </span>
                      ) : (
                        <span className="rounded-full bg-warning-subtle px-2 py-0.5 text-xs font-medium text-warning-subtle-foreground">
                          Unverified
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2 [&>*]:flex-1 sm:[&>*]:flex-none">
                  {!a.is_primary ? (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => void onSetPrimary(a.id)}
                      disabled={busyId === a.id}
                      className={cn(busyId === a.id ? "opacity-60" : "")}
                    >
                      <Star className="h-3.5 w-3.5" />
                      Make primary
                    </Button>
                  ) : null}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => void onDelete(a.id)}
                    disabled={busyId === a.id}
                    className="text-danger-subtle-foreground hover:text-danger"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                    Remove
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
